import React, { ChangeEvent, FC, useMemo } from 'react'
import { EditorState } from 'draft-js'
import CopyToClipBoard from 'react-copy-to-clipboard'
import { ImagesCrop, PriceForm, SelectBox, TextInput, WysiwygEditor } from 'components/Form'
import { Spacer } from 'components/Utility'
import { FormLabel } from 'components/Label'
import { SubDescription } from 'components/Typography'
import { FlexRow } from 'components/Flex'
import { BlueButton } from 'components/Button'
import { Tooltip, TooltipWrapper } from 'components/Tooltip'
import { useTooltip } from 'hooks/form'
import { CategoryType } from 'types/category'

type Props = {
  breadId: string
  storeId: string
  categories: CategoryType[]
  categoryId: string
  currentDescriptionLength: number
  description: EditorState | undefined
  deleteImageUrls: (index: number) => void
  handleName: (event: ChangeEvent<HTMLInputElement>) => void
  handleDescription: (editorState: EditorState) => void
  handleImageUrls: (value: string) => void
  handlePrice: (event: ChangeEvent<HTMLInputElement>) => void
  handleShortName: (event: ChangeEvent<HTMLInputElement>) => void
  imageUrls: string[]
  name: string
  price: number
  rearrangeImages: (index: number, type: 'prev' | 'next') => void
  replaceImage: (src: string, dist: string) => void
  selectCategoryName: (value: string) => void
  shortName: string
}

const ProductEditBaseSection: FC<Props> = React.memo((props) => {
  const {
    breadId,
    storeId,
    categories,
    categoryId,
    currentDescriptionLength,
    description,
    deleteImageUrls,
    handleName,
    handleDescription,
    handleImageUrls,
    handlePrice,
    handleShortName,
    imageUrls,
    name,
    price,
    rearrangeImages,
    replaceImage,
    selectCategoryName,
    shortName,
  } = props

  const { tooltip, openTooltip } = useTooltip()

  const categoryOptions = useMemo(() => {
    if (!categories || categories.length === 0) return [{ label: '', value: '' }]

    return categories.map((category) => {
      return { label: category.name, value: category.category_ref.id }
    })
  }, [categories])

  return (
    <>
      {breadId && (
        <>
          <FlexRow alignItems={'end'} justifyContent={'start'}>
            <TextInput
              disabled={true}
              id={'name'}
              label={'商品ID'}
              required={true}
              size={'small'}
              type={'text'}
              value={breadId}
            />
            <Spacer size={16} />
            <CopyToClipBoard text={breadId}>
              <BlueButton label={'コピー'} onClick={openTooltip} size={'narrow'} type={'outlined'} />
            </CopyToClipBoard>
            <TooltipWrapper open={tooltip}>
              <Tooltip direction={'top'} label={'Copied!'} />
            </TooltipWrapper>
          </FlexRow>
          <Spacer size={28} />
        </>
      )}
      <TextInput
        disabled={false}
        id={'name'}
        label={'商品名'}
        onChange={handleName}
        required={true}
        size={'small'}
        type={'text'}
        value={name}
      />
      <Spacer size={28} />
      <FormLabel id={'short-name'} label={'商品の短縮名'} required={false} />
      <Spacer size={8} />
      <SubDescription
        description={
          'このフィールドが入力されている場合、管理画面上の売上確認を除くページで、こちらの表記が優先して表示されます。'
        }
      />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        onChange={handleShortName}
        required={false}
        size={'small'}
        type={'text'}
        value={shortName}
      />
      <Spacer size={28} />
      <FormLabel id={'image'} label={'商品画像'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'画像をドラッグアンドドロップしてもアップロードできます。'} />
      <Spacer size={8} />
      <ImagesCrop
        folderPath={`breads/${storeId}/${breadId}`}
        deleteImageUrls={deleteImageUrls}
        handleImageUrls={handleImageUrls}
        rearrangeImages={rearrangeImages}
        imageUrls={imageUrls}
        imageType={'product'}
      />
      <Spacer size={28} />
      <FormLabel id={'description'} label={'商品説明'} required={true} />
      <Spacer size={8} />
      <WysiwygEditor
        currentLength={currentDescriptionLength}
        editorState={description}
        folderPath={`breads/${storeId}/${breadId}/description`}
        maxLength={3000}
        onEditorStateChange={handleDescription}
        replaceImage={replaceImage}
      />
      <Spacer size={56} />
      <FormLabel id={'category'} label={'商品カテゴリー'} required={true} />
      <Spacer size={8} />
      <SelectBox options={categoryOptions} select={selectCategoryName} value={categoryId} />
      <Spacer size={28} />
      <PriceForm id={'price'} handlePrice={handlePrice} price={price} />
    </>
  )
})

export default ProductEditBaseSection
